import { zodResolver } from "@hookform/resolvers/zod";
import { TFunction } from "i18next";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Select, { ActionMeta, SingleValue, StylesConfig } from "react-select";
import { z } from "zod";
import useSecurityQuestionCheck from "../../../api/auth/forgetPassword/useSecurityQuestionCheck";
import {
  Loader,
  ConfirmationPopup,
  GeneralRadio,
  GeneralButton,
  GeneralInput,
} from "../../../components";
import { EndPoints } from "../../../Constants/endpoints";
import i18n from "../../../i18n";
import {
  AccountType,
  PatientDetails,
  SpecialtyType,
} from "../../../interface/GeneralResponse";
import AuthLayout from "../../../Layout/AuthLayout";
import { RootState } from "../../../redux/store";
import { useGetData } from "../../../hooks";

export interface OptionType {
  key: string | number;
  label: string;
  value: string | number;
  hasChildren?: boolean;
  patientData?: PatientDetails;
}

interface RadioOption {
  key: string | number;
  value: number;
  label: string;
}

export interface FormValues {
  FullName: string;
  AccountTypeId: number;
  SpecialtyId: number | null;
  SubspecialtyId: number | null;
}

export const customStyles: StylesConfig<OptionType, false> = {
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    border: "1px solid #919EAB33",
    outline: "none",
    boxShadow: "none",
    borderRadius: ".625rem",
    overflow: "hidden",
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
  container: (provided) => ({
    ...provided,
    padding: "0",
    height: "3.5rem",
    width: "auto",
    background: "none",
    border: "none",
  }),
  control: (provided, state) => ({
    ...provided,
    cursor: "pointer",
    padding: "2px 8px",
    border: state.isFocused ? "2px solid #065EEA" : "2px solid transparent",
    borderRadius: "0.625rem",
    textAlign: "start",
    width: "100%",
    background: "#F8FAFF",
    margin: "0",
    color: "#566B8D",
    height: "3.5rem",
  }),
  option: (provided, state) => ({
    ...provided,
    textAlign: "start",
    padding: "12px 16px",
    color: "#566B8D",
    cursor: "pointer",
    backgroundColor: state.isSelected
      ? "#f8faff"
      : state.isFocused
        ? "#f8faff"
        : "transparent",
    ":hover": {
      backgroundColor: "#f8faff",
    },
    ":active": {
      backgroundColor: "transparent",
    },
  }),
  menuList: (provided) => ({
    ...provided,

    borderRadius: "0",
    padding: 0,
    maxHeight: "10rem",
    overflow: "auto",
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    display: "none",
    backgroundColor: "transparent",
  }),
  singleValue: (provided, state) => ({
    ...provided,
    color: "#566B8D", //state.isDisabled ? "#A9A9A9" : "#065EEA", // Custom color for the default value
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#566B8D", // Color for the placeholder text
  }),
  // You can add more custom styles here as needed
};

const SecurityQuestionCheck: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setting } = useParams();
  const { Id } = useSelector((state: RootState) => state.forgetPassword);

  const [isModalOpen, setIsModalOpen] = useState(false);

  // State variables for specialty and subspecialty
  const [selectedSpecialtyId, setSelectedSpecialtyId] = useState<number | null>(
    null,
  );
  const [hasSubSpecialties, setHasSubSpecialties] = useState<boolean>(false);
  const [subspecialtyOptions, setSubspecialtyOptions] = useState<OptionType[]>(
    [],
  );

  // Determine current language
  const currentLang = i18n.language.startsWith("ar") ? "ar" : "en";

  // Redirect to login if Id is not set
  useEffect(() => {
    if (Id == null || Id === undefined) {
      navigate("/login");
    }
  }, [Id, navigate]);

  // Function to create the validation schema with dynamic error messages
  const createValidationSchema = (t: TFunction) =>
    z.object({
      FullName: z.string().min(1, t("what_is_your_full_name")),
      AccountTypeId: z
        .number({ required_error: t("choose_your_account_type") })
        .min(1, t("choose_your_account_type")),
      SpecialtyId: z
        .number()
        .nullable()
        .refine((val) => val !== null && val >= 1, {
          message: t("select_your_specialty"),
        }),
      SubspecialtyId: z.number().nullable(), // Optional field
    });

  // Recreate the schema whenever the language changes
  const validationSchema = useMemo(() => createValidationSchema(t), [t]);

  const methods = useForm<FormValues>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      FullName: "",
      SpecialtyId: null,
      AccountTypeId: -1,
      SubspecialtyId: null,
    },
  });

  const {
    handleSubmit,
    formState: { errors, isSubmitted },
    watch,
    resetField,
    trigger,
    setValue,
  } = methods;

  const selectedAccountType = watch("AccountTypeId") || -1;
  const selectedSpecialty = watch("SpecialtyId");

  const { data: accountTypesData } = useGetData<AccountType[]>(
    EndPoints.SelectAccountType,
  );

  // Fetch specialties based on selected account type
  const { data: specialtiesData } = useGetData<SpecialtyType[]>(
    EndPoints.SelectSpecialtyType,
    {
      filter:
        selectedAccountType !== -1
          ? { AccountTypeId: selectedAccountType.toString() }
          : undefined,
    },
  );

  // Fetch subSpecialties based on selected specialty
  const { data: subSpecialtiesData } = useGetData<SpecialtyType[]>(
    EndPoints.SelectSpecialtyType,
    {
      enabled: !!selectedSpecialtyId,
      filter: selectedSpecialtyId
        ? { ParentSpecialtyId: selectedSpecialtyId.toString() }
        : undefined,
    },
  );

  // Memoize account types options based on current language
  const accountTypesOptions = useMemo<RadioOption[]>(() => {
    return (
      accountTypesData?.Data.map((type) => ({
        key: type.Id, // Added key
        value: type.Id,
        label: currentLang === "ar" ? type.NameAr : type.NameEn,
      })) || []
    );
  }, [accountTypesData, currentLang]);

  // Memoize specialties options including hasChildren
  const specialtiesOptions = useMemo<OptionType[]>(() => {
    return (
      specialtiesData?.Data.map((specialty) => ({
        key: specialty.Id, // Added key
        value: specialty.Id,
        label: currentLang === "ar" ? specialty.NameAr : specialty.NameEn,
        hasChildren: specialty.HasChildren,
      })) || []
    );
  }, [specialtiesData, currentLang]);

  // Handle account type change to reset fields
  useEffect(() => {
    resetField("SpecialtyId");
    resetField("SubspecialtyId");
    setHasSubSpecialties(false);
    setSubspecialtyOptions([]);
  }, [selectedAccountType, resetField]);

  // Handle specialty change
  useEffect(() => {
    if (selectedSpecialty && selectedSpecialty >= 1) {
      const selectedOption = specialtiesOptions.find(
        (option) => option.value === selectedSpecialty,
      );
      if (selectedOption && selectedOption.hasChildren) {
        setHasSubSpecialties(true);
        setSelectedSpecialtyId(selectedSpecialty);
        setValue("SubspecialtyId", null);
      } else {
        setHasSubSpecialties(false);
        setSubspecialtyOptions([]);
        setValue("SubspecialtyId", null);
      }
    } else {
      setHasSubSpecialties(false);
      setSubspecialtyOptions([]);
      setValue("SubspecialtyId", null);
    }
  }, [selectedSpecialty, specialtiesOptions, setValue]);

  // Memoize subspecialty options based on current language
  useEffect(() => {
    if (subSpecialtiesData && Array.isArray(subSpecialtiesData.Data)) {
      const options: OptionType[] = subSpecialtiesData.Data.map(
        (sub: SpecialtyType) => ({
          key: sub.Id, // Added key
          value: sub.Id,
          label: currentLang === "ar" ? sub.NameAr : sub.NameEn,
        }),
      );
      setSubspecialtyOptions(options);
    } else {
      setSubspecialtyOptions([]);
    }
  }, [subSpecialtiesData, currentLang]);

  // Re-trigger validation on language change
  useEffect(() => {
    if (isSubmitted) {
      trigger();
    }
  }, [t, trigger, isSubmitted]);

  // Handle browser navigation (back button)
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      event.preventDefault();
      setIsModalOpen(true);
    };
    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [navigate]);

  // Handle page unload (refresh, close tab)
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (methods.formState.isDirty && !isModalOpen) {
        event.preventDefault();
        event.returnValue = t("unsaved_changes_warning");
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [methods.formState.isDirty, isModalOpen, t]);

  // Handle form submission using mutation hook
  const { mutate, isPending: isSubmitting } = useSecurityQuestionCheck();

  const onSubmit: SubmitHandler<FormValues> = useCallback(
    (data: FormValues) => {
      if (Id == null || Id === undefined) {
        navigate("/login");
        return;
      }

      const requestData = {
        Id: Id,
        FullName: data.FullName,
        SpecialtyId: data.SubspecialtyId || data.SpecialtyId!,
        AccountTypeId: data.AccountTypeId,
      };

      mutate(requestData);
    },
    [Id, mutate, navigate],
  );

  // Handle confirmation popup for navigation
  const handleConfirmNavigation = () => {
    setIsModalOpen(false);
    navigate(
      setting === "setting" ? "/forget_password/setting" : "/forgot-password",
    );
  };

  const handleCancelNavigation = () => {
    setIsModalOpen(false);
    window.history.pushState(null, "", window.location.href);
  };

  return (
    <FormProvider {...methods}>
      <AuthLayout
        title={t("verify_that_is_you")}
        description={t("please_answer_these_simple_questions")}
        lottieImage={null}
      >
        <form
          className="flex w-full flex-col gap-6"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="">
            <label
              className="text-body-text mb-2 inline-block text-sm"
              htmlFor="FullName"
            >
              {t("what_is_your_full_name")}
            </label>
            <GeneralInput
              type="text"
              name="FullName"
              charactersOnly
              placeholder={t("full_name")}
            />
          </div>

          {/* Account Types Radio Buttons */}
          <Controller
            name="AccountTypeId"
            control={methods.control}
            render={({ field }) => (
              <div className="flex flex-col gap-2">
                <p className="text-body-text mb-3 text-sm">
                  {t("choose_your_type")}
                </p>
                <div className="flex gap-20 pe-4 ps-2">
                  {accountTypesOptions.map((option) => (
                    <GeneralRadio
                      key={option.key}
                      name={field.name}
                      label={option.label}
                      value={option.value}
                      checked={field.value === option.value}
                      onClick={(e: any) =>
                        field.onChange(Number(e.target.value))
                      }
                    />
                  ))}
                </div>
              </div>
            )}
          />
          {errors.AccountTypeId && (
            <p className="text-sm text-error">{errors.AccountTypeId.message}</p>
          )}

          {/* Specialty Selection */}
          {selectedAccountType !== -1 && (
            <div>
              <label
                className="text-body-text mb-2 inline-block text-sm"
                htmlFor="SpecialtyId"
              >
                {t("enter_your_specialty")}
              </label>
              <Controller
                name="SpecialtyId"
                control={methods.control}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={specialtiesOptions} // Now includes 'key'
                    placeholder={t("specialty")}
                    onChange={(
                      option: SingleValue<OptionType>,
                      actionMeta: ActionMeta<OptionType>,
                    ) => {
                      field.onChange(option ? option.value : null);
                    }}
                    value={
                      specialtiesOptions.find(
                        (option) => option.value === field.value,
                      ) || null
                    }
                    styles={customStyles} // Apply customStyles
                    menuPortalTarget={document.body} // Optional: render menu in portal
                  />
                )}
              />
              {errors.SpecialtyId && (
                <p className="text-sm text-error">
                  {errors.SpecialtyId.message}
                </p>
              )}
            </div>
          )}
          {/* Subspecialty Selection */}
          {hasSubSpecialties && (
            <div>
              <label
                className="text-body-text mb-2 inline-block text-sm"
                htmlFor="SubspecialtyId"
              >
                {t("select_subspecialty")}
              </label>
              <Controller
                name="SubspecialtyId"
                control={methods.control}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={subspecialtyOptions} // Now includes 'key'
                    placeholder={t("sub_specialty")}
                    onChange={(
                      option: SingleValue<OptionType>,
                      actionMeta: ActionMeta<OptionType>,
                    ) => {
                      field.onChange(option ? option.value : null);
                    }}
                    value={
                      subspecialtyOptions.find(
                        (option) => option.value === field.value,
                      ) || null
                    }
                    styles={customStyles} // Apply customStyles
                    menuPortalTarget={document.body} // Optional: render menu in portal
                  />
                )}
              />
              {errors.SubspecialtyId && (
                <p className="text-sm text-error">
                  {errors.SubspecialtyId.message}
                </p>
              )}
            </div>
          )}

          <div className="mt-2">
            <GeneralButton
              type="submit"
              title={t("submit")}
              color="blue"
              block
              disabled={isSubmitting}
            />
          </div>
          {isSubmitting && <Loader />}
        </form>
      </AuthLayout>

      {/* Confirmation Popup */}
      {isModalOpen && (
        <ConfirmationPopup
          isModalOpen={isModalOpen}
          title={t("all_info_will_be_lost")}
          description={t("are_you_sure_to_do_action")}
          onClose={handleCancelNavigation}
          onConfirm={handleConfirmNavigation}
          ariaLabel={t("confirm_navigation")}
        />
      )}
    </FormProvider>
  );
};

export default SecurityQuestionCheck;
